import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PaymentTransactionHistory } from '../../services/payment.service';
import { PaymentMethodCode } from '../../shared/types/payment-method.enum';
import { formatCardPaymentLabel } from '../../shared/utils/card-payment-label.util';

export interface PaymentLineItem {
  label: string;
  amount: string;
}

/**
 * Displays a payment transaction card that summarizes a transaction date, line items, total amount, payment method and optionally allows downloading an invoice.
 * @component
 * @selector app-payment-transaction-card
 * @example
 *   <app-payment-transaction-card
 *     [date]="txnDate"
 *     [items]="lineItems"
 *     [totalLabel]="'Total Amount Paid'"
 *     [totalAmount]="formattedTotal"
 *     [downloadable]="true"
 *     [transactionData]="transaction"
 *     (downloadInvoice)="onDownload($event)">
 *   </app-payment-transaction-card>
 * @input {string} date - The display date for the transaction. Default: ''.
 * @input {PaymentLineItem[]} items - Array of line items to render for the transaction. Default: [].
 * @input {string} totalLabel - Label shown above the total amount. Default: 'Total Amount Paid'.
 * @input {string} totalAmount - Formatted total amount string to display.
 * @input {boolean} downloadable - When true, enables the invoice download action/UI. Default: true.
 * @input {PaymentTransactionHistory | null} transactionData - Full transaction data model used for details such as payment method and download payload. Default: null.
 * @output {EventEmitter<PaymentTransactionHistory>} downloadInvoice - Emitted when the user requests to download the invoice; payload is the transactionData.
 * @method onDownloadInvoice() - Triggered by the download action; emits downloadInvoice only if transactionData is present.
 * @method formatPaymentMethod(paymentMethod: string | undefined): string - Normalizes a raw payment method identifier into a human readable label. Returns '-' when undefined, maps 'card_present' to 'Card', replaces underscores with spaces and capitalizes words otherwise.
 * @property {string} paymentMethod - Getter that returns the formatted payment method for the current transactionData (calls formatPaymentMethod). Note: it also logs the transactionData to the console for debugging.
 * @notes
 *   - The component is standalone and imports CommonModule; template and styles are provided in external files.
 *   - No Angular lifecycle hooks (ngOnInit, ngOnChanges, etc.) are used within this component.
 */
@Component({
  selector: 'app-payment-transaction-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-transaction-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./payment-transaction-card.component.scss'],
})
export class PaymentTransactionCardComponent {
  @Input() date = '';
  @Input() items: PaymentLineItem[] = [];
  @Input() totalLabel = 'Total Amount Paid';
  @Input() totalAmount = '';
  @Input() downloadable = true;
  @Input() transactionData: PaymentTransactionHistory | null = null;
  @Output() downloadInvoice = new EventEmitter<PaymentTransactionHistory>();
  @Output() printInvoice = new EventEmitter<PaymentTransactionHistory>();

  onDownloadInvoice(): void {
    if (this.transactionData) {
      this.downloadInvoice.emit(this.transactionData);
    }
  }

  onPrintInvoice(): void {
    if (this.transactionData) {
      this.printInvoice.emit(this.transactionData);
    }
  }

  /**
   * Format a payment method identifier into a user-friendly display string.
   * @example
   * ormatPaymentMethod('card_present')
   * 'Card'
   * @param {{string|undefined}} {{paymentMethod}} - Payment method identifier that may be undefined; underscores will be replaced with spaces and words capitalized.
   * @returns {{string}} Formatted payment method string for display, or '-' when input is falsy.
   **/
  formatPaymentMethod(paymentMethod: string | undefined): string {
    if (!paymentMethod) return '-';
    if (paymentMethod.toLowerCase() === PaymentMethodCode.CardPresent) {
      return 'Card';
    }
    // Capitalize first letter and replace underscores with spaces
    return paymentMethod
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  get paymentMethod(): string {
    const payload = this.transactionData?.payload;
    if (payload?.event === 'payment_method_added' && (payload.display || payload.last4)) {
      return payload.display || this.formatPaymentMethod(this.transactionData?.payment_method);
    }
    // The card this payment was charged on, when it was stored with the payment: a membership's
    // renewals can each be on a different card.
    return (
      formatCardPaymentLabel(payload?.brand, payload?.last4) ??
      this.formatPaymentMethod(this.transactionData?.payment_method)
    );
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
